import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  VStack,
  HStack,
  Box,
  Heading,
  Text,
  Button,
  Badge,
  NativeSelect,
  Spinner,
  Alert,
  useDisclosure,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiPlus, FiTrash2 } from 'react-icons/fi'
import { useCampaigns, useCreateCampaign, useDeleteCampaign } from '../hooks/useCampaign'
import { CampaignFormModal } from '../components/campaign/CampaignFormModal'
import { ResponsiveTable, FilterBar, type ResponsiveColumn } from '../components/shared'
import type { CampaignStatus, CampaignSummaryDto } from '../types/campaign'

const statusBadgeColor: Record<CampaignStatus, string> = {
  Draft: 'gray',
  Active: 'green',
  Completed: 'purple',
  Archived: 'orange',
}

export function CampaignListPage() {
  const navigate = useNavigate()
  const cancelRef = useRef<HTMLButtonElement>(null)
  const createModal = useDisclosure()

  const [statusFilter, setStatusFilter] = useState<CampaignStatus | ''>('')
  const [search, setSearch] = useState('')

  const { data: campaigns, isLoading, error } = useCampaigns({
    status: statusFilter || undefined,
    search: search || undefined,
  })
  const createMutation = useCreateCampaign()
  const deleteMutation = useDeleteCampaign()

  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null)

  const handleCreate = async (data: { label: string; description?: string }) => {
    try {
      const campaign = await createMutation.mutateAsync(data)
      toaster.create({
        title: 'Campaign created',
        description: `"${campaign.label}" has been created.`,
        type: 'success',
        duration: 4000,
      })
      createModal.onClose()
      navigate(`/dashboard/campaigns/${campaign.id}`)
    } catch {
      toaster.create({
        title: 'Create failed',
        description: 'An error occurred while creating the campaign.',
        type: 'error',
        duration: 5000,
      })
    }
  }

  const openArchiveDialog = (campaign: CampaignSummaryDto) => {
    setDeleteTarget({ id: campaign.id, label: campaign.label })
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteMutation.mutateAsync(deleteTarget.id)
      toaster.create({
        title: 'Campaign archived',
        description: `"${deleteTarget.label}" has been archived.`,
        type: 'success',
        duration: 4000,
      })
    } catch {
      toaster.create({
        title: 'Archive failed',
        description: 'An error occurred while archiving the campaign.',
        type: 'error',
        duration: 5000,
      })
    } finally {
      setDeleteTarget(null)
    }
  }

  const columns: ResponsiveColumn<CampaignSummaryDto>[] = [
    {
      header: 'Name',
      mobileRole: 'title',
      cell: (r) => (
        <VStack align="start" gap={0}>
          <Text fontWeight="medium">{r.label}</Text>
          {r.description && (
            <Text fontSize="xs" color="gray.500" lineClamp={1}>
              {r.description}
            </Text>
          )}
        </VStack>
      ),
    },
    {
      header: 'Status',
      mobileRole: 'badge',
      cell: (r) => <Badge colorPalette={statusBadgeColor[r.status]}>{r.status}</Badge>,
    },
    {
      header: 'Turfs',
      mobileRole: 'meta',
      isNumeric: true,
      cell: (r) => <Text fontSize="sm">{r.totalTurfCount}</Text>,
    },
    {
      header: 'Contacts',
      mobileRole: 'meta',
      isNumeric: true,
      cell: (r) => <Text fontSize="sm">{r.totalContactCount.toLocaleString()}</Text>,
    },
    {
      header: 'Completion',
      mobileRole: 'meta',
      cell: (r) => {
        const completionPct = r.totalContactCount > 0
          ? Math.round((r.visitedContactCount / r.totalContactCount) * 100)
          : 0
        return (
          <Text fontSize="sm">
            {r.visitedContactCount}/{r.totalContactCount}
            <Text as="span" color="gray.400" ml={1}>
              ({completionPct}%)
            </Text>
          </Text>
        )
      },
    },
    {
      header: 'Created',
      mobileRole: 'meta',
      cell: (r) => (
        <Text fontSize="sm" color="gray.600">
          {new Date(r.createdAt).toLocaleDateString()}
        </Text>
      ),
    },
  ]

  return (
    <Container maxW="container.xl" py={8}>
      <VStack gap={6} align="stretch">
        <HStack justify="space-between" flexWrap="wrap" gap={3}>
          <Box>
            <Heading size="lg">Campaigns</Heading>
            <Text color="gray.500" mt={1}>
              Organize canvassing efforts under campaign workspaces
            </Text>
          </Box>
          <Button colorPalette="brand" onClick={createModal.onOpen}><FiPlus />New Campaign
                      </Button>
        </HStack>

        {/* Filters */}
        <FilterBar
          search={{
            value: search,
            onChange: setSearch,
            placeholder: 'Search by name...',
          }}
          filterCount={statusFilter !== '' ? 1 : 0}
        >
          <NativeSelect.Root size="sm">
            <NativeSelect.Field
              placeholder="All statuses"
              w={{ base: 'full', md: '180px' }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as CampaignStatus | '')}>
              <option value="Draft">Draft</option>
              <option value="Active">Active</option>
              <option value="Completed">Completed</option>
              <option value="Archived">Archived</option>
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </FilterBar>

        {isLoading && (
          <VStack py={12}>
            <Spinner size="lg" color="accent" />
            <Text color="gray.500">Loading campaigns...</Text>
          </VStack>
        )}

        {!!error && (
          <Alert.Root status="error" borderRadius="md">
            <Alert.Indicator />
            Failed to load campaigns. Please try again.
          </Alert.Root>
        )}

        {!isLoading && campaigns && campaigns.length === 0 && (
          <Box bg="surface" p={12} borderRadius="lg" shadow="md" textAlign="center">
            <Text color="gray.500" mb={4}>
              {search || statusFilter
                ? 'No campaigns match your filters.'
                : 'No campaigns yet. Create your first campaign to start organizing canvassing efforts.'}
            </Text>
            {!search && !statusFilter && (
              <Button colorPalette="brand" onClick={createModal.onOpen}><FiPlus />Create Your First Campaign
                              </Button>
            )}
          </Box>
        )}

        {!isLoading && campaigns && campaigns.length > 0 && (
          <ResponsiveTable
            columns={columns}
            rows={campaigns}
            keyFn={(r) => r.id}
            onRowClick={(r) => navigate(`/dashboard/campaigns/${r.id}`)}
            rowActions={[
              {
                label: 'Archive',
                icon: FiTrash2,
                color: 'red.500',
                onClick: (r) => openArchiveDialog(r),
              },
            ]}
          />
        )}
      </VStack>

      {/* Delete Confirmation Dialog */}
      <Dialog.Root
        open={!!deleteTarget}
        initialFocusEl={() => cancelRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            setDeleteTarget(null);
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  Archive Campaign
                </Dialog.Header>
                <Dialog.Body>
                  Are you sure you want to archive <strong>"{deleteTarget?.label}"</strong>?
                  The campaign and its data will be preserved but hidden from the active list.
                </Dialog.Body>
                <Dialog.Footer>
                  <Button ref={cancelRef} onClick={() => setDeleteTarget(null)}>
                    Cancel
                  </Button>
                  <Button
                    colorPalette="red"
                    onClick={handleConfirmDelete}
                    ml={3}
                    loading={deleteMutation.isPending}
                  >
                    Archive
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>

      <CampaignFormModal
        isOpen={createModal.open}
        onClose={createModal.onClose}
        onSubmit={handleCreate}
        isLoading={createMutation.isPending}
      />
    </Container>
  );
}
